import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { BadgesLevels, CopyLink, RowSelectorTd, Status } from 'components/misc'
import { Data, Table } from 'components/layout/Table'
import { participantsGrid as participantsGridLayout } from 'constants/layout'
import { BulkChangeParticipants } from 'containers/bulkChange'
import { FormattedMessage } from 'react-intl'
import config from 'config'
import get from 'lodash/get'
import { AWARD_MODES, CONFIGURATION_KEYS } from '../../../../constants/enums'
import { initialize } from 'redux-form'
import { getGridLabels } from '../../../../helpers/labelHelper'
import { Export } from '../../../../containers/misc'

export default class ParticipantsGridTable extends PureComponent {
  static propTypes = {
    participants: PropTypes.object.isRequired,
    participantsGrid: PropTypes.object.isRequired,
    permissions: PropTypes.object.isRequired,
    organizationHierarchyId: PropTypes.number,
    setParticipantsGridParams: PropTypes.func,
    exportParticipantsGrid: PropTypes.func.isRequired,
    setParticipantSelectedTo: PropTypes.func.isRequired,
    selectableParticipantIds: PropTypes.array,
    areAllParticipantsSelected: PropTypes.bool,
    bulkSelectParticipants: PropTypes.func,
    showLastActivity: PropTypes.bool,
    setCommonLimit: PropTypes.func,
    initialize: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    setFilter: PropTypes.func.isRequired,
    isAssessorParticipants: PropTypes.func.bool,
  }
  static defaultProps = {
    permissions: {}
  }

  getOrganizationLink = ({id, name}) => {
    return <Link to={`${config.publicPath}/award-units/${id}/detail`}>{name}</Link>
  }

  addLabelToFilter = label => {
    let initValues = []

    initValues.push({ value: label.id, label: label.name })
    this.props.dispatch(initialize(
      'participants-grid-filter',
      { 'participantLabel@or' : initValues }
    ))

    this.props.setFilter(`{"participantLabel@or": [{"value":${label.id},"label":"${label.name}"}]}`)
  }

  addAwardLabelToFilter = label => {
    let initValues = []

    initValues.push({ value: label.id, label: label.name })
    this.props.dispatch(initialize(
      'participants-grid-filter',
      { 'awardLabel@or' : initValues }
    ))

    this.props.setFilter(`{"awardLabel@or": [{"value":${label.id},"label":"${label.name}"}]}`)
  }

  render() {
    const {
      selectableParticipantIds: selectableIds,
      permissions,
      setParticipantsGridParams,
      areAllParticipantsSelected,
      bulkSelectParticipants,
      organizationHierarchyId,
      setParticipantSelectedTo,
      showLastActivity,
      setCommonLimit,
      participants,
      configuration,
      activeOrganization,
      isAssessorParticipants
    } = this.props

    let loading = get(participants, 'grid.loading')
    let list = get(participants, 'grid.list')
    let offset = get(participants, 'grid.offset')
    let limit = get(participants, 'grid.limit')
    let pages = get(participants, 'grid.pages')
    let sort = get(participants, 'grid.sort')
    let count = get(participants, 'grid.countLoaded') && get(participants, 'grid.count')
    let selectedIds = get(participants, 'selectedIds') || []

    const isLabelsEnabled = get(configuration, `valuesByOrganizationId[${activeOrganization.id}][${CONFIGURATION_KEYS.enableLabels.id}].booleanValue`)

    const isAwardLabelsEnabled = get(configuration, `valuesByOrganizationId[${activeOrganization.id}][${CONFIGURATION_KEYS.enableAwardLabels.id}].booleanValue`)

    const ExportComponent = (
      <Export
        exportName="Participants"
        toExport={this.props.exportParticipantsGrid}
        exportSchemeValue="participants-grid"
        count={participants && participants.grid && participants.grid.count}
        exportLimit={false}
      />
    )

    const limitOptions = [
      {
        value: 5,
        label: 5
      },
      {
        value: 10,
        label: 10
      },
      {
        value: 20,
        label: 20
      },
      {
        value: 50,
        label: 50
      },
      {
        value: 100,
        label: 100
      }
    ]

    return (
      <div>
        {<BulkChangeParticipants selectedIds={selectedIds} />}

        <Table
          loading={loading}
          length={list.length}
          offset={offset}
          limit={limit}
          limitOptions={limitOptions}
          onSetLimit={setCommonLimit}
          pages={pages}
          count={count}
          sort={sort}
          ths={participantsGridLayout.tableHeaders({ organizationHierarchyId, showLastActivity, isLabelsEnabled, isAwardLabelsEnabled })}
          areAllSelected={areAllParticipantsSelected ? true : undefined}
          bulkSelect={() => bulkSelectParticipants(selectedIds, selectableIds)}
          setParams={setParticipantsGridParams}
          permissions={permissions}
          exportComponent={isAssessorParticipants ? null : ExportComponent}
        >

          <Data>
            {list.map(
              ({
                 id,
                 person,
                 award,
                 awardLeader
               }) => {
                const globalId = get(person, 'globalId')
                const firstName = get(person, 'firstName')
                const lastName = get(person, 'lastName')
                const email = get(person, 'email')
                const hasBronzeAward = get(person, 'hasBronzeAward')
                const hasSilverAward = get(person, 'hasSilverAward')
                const hasGoldAward = get(person, 'hasGoldAward')
                const lastLogDate = get(person, 'lastLogDate')
                const personLabels = get(person, 'labels')
                const personStateValue = get(person, 'state.type')
                const personStateText = get(person, 'state.value')

                const currentAwardLeaderId = get(awardLeader, 'id')
                const currentAwardLeaderName = get(awardLeader, 'name')

                const awardLabels = get(award, 'labels')
                const organization = get(award, 'organization')
                const awardStateValue = get(award, 'state.type')
                const awardStateText = get(award, 'state.value')
                const awardModeValue = get(award, 'mode.type')
                const isLastAwardOffline = awardModeValue === AWARD_MODES.offline.id

                const organizationId = get(organization, 'id')
                const organizationName = get(organization, 'name')
                const nao = get(organization, 'nao')

                const naoName = get(nao, 'name')

                return (
                  <tr key={id}>
                    <RowSelectorTd
                      enabled={selectableIds && selectableIds.includes(id) ? true : undefined}
                      selected={selectedIds && selectedIds.includes(id) ? true : undefined}
                      setSelected={val => setParticipantSelectedTo(id, val)}
                    />
                    <td>
                      <Link
                        to={{ pathname: `${config.publicPath}/participants/${id}/detail` }}
                      >
                        {globalId}
                      </Link>
                    </td>
                    <td>{firstName}</td>
                    <td>{lastName}</td>
                    <td>{email} {email && <CopyLink text={email} />}</td>

                    <td>
                      <BadgesLevels
                        hasGold={hasGoldAward}
                        hasSilver={hasSilverAward}
                        hasBronze={hasBronzeAward}
                        participantId={id}
                        small
                      />
                    </td>
                    {showLastActivity &&
                      <td>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isLastAwardOffline ? <FormattedMessage
                          id="logs.offline"
                          defaultMessage="Offline"
                        /> : (lastLogDate
                          ? moment(lastLogDate).fromNow()
                          : <FormattedMessage
                            id="logs.no_logs"
                            defaultMessage="No logs"
                          />)
                        }
                      </td>
                    }
                    {isLabelsEnabled && <td>
                      {personLabels && getGridLabels(personLabels, this.addLabelToFilter, false, activeOrganization)}
                    </td> }
                    {isAwardLabelsEnabled && <td>
                      {awardLabels && getGridLabels(awardLabels, this.addAwardLabelToFilter, false, activeOrganization)}
                    </td> }
                    <td>
                      {currentAwardLeaderId ? (
                        <Link to={`${config.publicPath}/leaders/${currentAwardLeaderId}/detail`}>
                          {currentAwardLeaderName}
                        </Link>
                      ) : (
                        '---'
                      )}
                    </td>
                    {(permissions.isIf || permissions.isNaoStaff) && (
                      <td>
                        {this.getOrganizationLink({id: organizationId, name: organizationName})}
                      </td>
                    )}
                    {permissions.isIf && (
                      <td>
                        {naoName}
                      </td>
                    )}
                    <td>
                      <Status state={{ value: personStateValue, translationText: personStateText }} />
                      <br />
                      <small>
                        {awardStateValue && awardStateText}
                      </small>
                    </td>
                  </tr>
                )
              }
            )}
          </Data>
        </Table>
      </div>
    )
  }
}
